import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "asale" }
const _hoisted_2 = {
  key: 0,
  class: "asale__error"
}
const _hoisted_3 = {
  class: "sui-card sui-card--flush",
  "data-tour": "admin-sales-list"
}
const _hoisted_4 = { class: "sui-toolbar" }
const _hoisted_5 = { class: "asale__title" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = {
  key: 0,
  class: "sui-table-wrap"
}
const _hoisted_8 = { class: "sui-table" }
const _hoisted_9 = { class: "sui-table__strong" }
const _hoisted_10 = { class: "sui-table__sub" }
const _hoisted_11 = { class: "is-center" }
const _hoisted_12 = { class: "is-center" }
const _hoisted_13 = { class: "asale__actions" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = {
  key: 1,
  class: "sui-empty"
}
const _hoisted_17 = ["onKeydown"]
const _hoisted_18 = {
  class: "asale__dialog",
  role: "dialog",
  "aria-modal": "true",
  "aria-labelledby": "asale-title",
  tabindex: "-1"
}
const _hoisted_19 = { class: "asale__head" }
const _hoisted_20 = { id: "asale-title" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = { class: "asale__body" }
const _hoisted_23 = { class: "asale__field" }
const _hoisted_24 = { class: "asale__dates" }
const _hoisted_25 = { class: "asale__field" }
const _hoisted_26 = { class: "asale__field" }
const _hoisted_27 = {
  key: 0,
  class: "asale__form-error"
}
const _hoisted_28 = { class: "asale__foot" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = ["disabled"]

import { onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  createAdminSale,
  deleteAdminSale,
  fetchAdminSales,
  updateAdminSale,
} from '@/api/admin'
import type { MallSale } from '@/api/mallProduct'
import { confirmDialog } from '@/composables/useConfirmDialog'

interface SaleDraft {
  code: string
  name: string
  start: string
  end: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminSalesPage',
  setup(__props) {

const sales = ref<MallSale[]>([])
const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const formError = ref('')
const editor = ref<SaleDraft | null>(null)

function todayKey() {
  const today = new Date()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${today.getFullYear()}-${month}-${day}`
}

function phaseLabel(sale: MallSale) {
  const today = todayKey()
  if (today < sale.start) return '예정'
  if (today > sale.end) return '종료'
  return '진행'
}

function phaseClass(sale: MallSale) {
  const label = phaseLabel(sale)
  if (label === '진행') return 'sui-badge--green'
  if (label === '예정') return 'sui-badge--blue'
  return 'sui-badge--gray'
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    sales.value = await fetchAdminSales()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '공동구매 특별할인을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function openNew() {
  formError.value = ''
  editor.value = { code: '', name: '', start: '', end: '' }
}

function openEdit(sale: MallSale) {
  formError.value = ''
  editor.value = { code: sale.code, name: sale.name, start: sale.start, end: sale.end }
}

function closeEditor() {
  if (saving.value) return
  editor.value = null
  formError.value = ''
}

async function onSave() {
  const draft = editor.value
  if (!draft || saving.value) return
  const name = draft.name.trim()
  if (!name) {
    formError.value = '특별할인 이름을 입력해 주세요.'
    return
  }
  if (!draft.start || !draft.end) {
    formError.value = '시작일과 종료일을 입력해 주세요.'
    return
  }
  if (draft.start > draft.end) {
    formError.value = '종료일은 시작일보다 빠를 수 없습니다.'
    return
  }
  saving.value = true
  formError.value = ''
  try {
    const body = { name, start: draft.start, end: draft.end }
    if (draft.code) await updateAdminSale(draft.code, body)
    else await createAdminSale(body)
    editor.value = null
    await load()
  } catch (error) {
    formError.value = error instanceof Error ? error.message : '특별할인을 저장하지 못했습니다.'
  } finally {
    saving.value = false
  }
}

async function onDelete(sale: MallSale) {
  const ok = await confirmDialog({
    title: '특별할인 삭제',
    message: `「${sale.name}」 특별할인을 삭제할까요?`,
    detail: '공동구매 상품은 이 특별할인에 묶여 있지 않습니다. 이미 나간 주문의 특별할인 이름은 그대로 남습니다.',
    confirmLabel: '삭제',
    tone: 'danger',
  })
  if (!ok) return
  errorMessage.value = ''
  try {
    await deleteAdminSale(sale.code)
    await load()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '특별할인을 삭제하지 못했습니다.'
  }
}

onMounted(() => {
  void load()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[10] || (_cache[10] = _createElementVNode("p", { class: "asale__lead" }, " 공동구매몰은 기간을 정해 여는 특별할인입니다. 이름은 설명절, 추석명절, 어린이날처럼 적거나 날짜 문구로 적어도 됩니다. ", -1)),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, [
          _createVNode(AppIcon, {
            icon: "mdi-alert-outline",
            size: 16
          }),
          _createTextVNode(" " + _toDisplayString(errorMessage.value), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("strong", _hoisted_5, "특별할인 " + _toDisplayString(sales.value.length) + "개", 1),
        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "sui-toolbar__spacer" }, null, -1)),
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn",
          disabled: loading.value,
          onClick: load
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-refresh",
            size: 16
          }),
          _cache[3] || (_cache[3] = _createTextVNode(" 새로고침 ", -1))
        ], 8, _hoisted_6),
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn sui-btn--primary",
          "data-tour": "admin-sales-new",
          onClick: openNew
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-plus",
            size: 16
          }),
          _cache[4] || (_cache[4] = _createTextVNode(" 특별할인 등록 ", -1))
        ])
      ]),
      (sales.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("table", _hoisted_8, [
              _cache[6] || (_cache[6] = _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", null, "이름"),
                  _createElementVNode("th", null, "시작"),
                  _createElementVNode("th", null, "종료"),
                  _createElementVNode("th", { class: "is-center" }, "상태"),
                  _createElementVNode("th", { class: "is-center" }, "관리")
                ])
              ], -1)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sales.value, (sale) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: sale.code
                  }, [
                    _createElementVNode("td", null, [
                      _createElementVNode("span", _hoisted_9, _toDisplayString(sale.name), 1),
                      _createElementVNode("span", _hoisted_10, _toDisplayString(sale.code), 1)
                    ]),
                    _createElementVNode("td", null, _toDisplayString(sale.start), 1),
                    _createElementVNode("td", null, _toDisplayString(sale.end), 1),
                    _createElementVNode("td", _hoisted_11, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["sui-badge", phaseClass(sale)])
                      }, _toDisplayString(phaseLabel(sale)), 3)
                    ]),
                    _createElementVNode("td", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("button", {
                          type: "button",
                          class: "sui-btn sui-btn--sm",
                          onClick: ($event: any) => (openEdit(sale))
                        }, "수정", 8, _hoisted_14),
                        _createElementVNode("button", {
                          type: "button",
                          class: "sui-btn sui-btn--sm sui-btn--ghost",
                          onClick: ($event: any) => (onDelete(sale))
                        }, " 삭제 ", 8, _hoisted_15)
                      ])
                    ])
                  ]))
                }), 128))
              ])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_16, [
            _createVNode(AppIcon, {
              icon: "mdi-calendar-blank-outline",
              size: 32
            }),
            _createElementVNode("p", null, _toDisplayString(loading.value ? '특별할인을 불러오는 중입니다…' : '등록된 공동구매 특별할인이 없습니다.'), 1)
          ]))
    ]),
    (editor.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "asale__modal",
          role: "presentation",
          onKeydown: _withKeys(_withModifiers(closeEditor, ["prevent"]), ["esc"])
        }, [
          _createElementVNode("div", {
            class: "asale__backdrop",
            onClick: closeEditor
          }),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("header", _hoisted_19, [
              _createElementVNode("h2", _hoisted_20, _toDisplayString(editor.value.code ? '특별할인 수정' : '특별할인 등록'), 1),
              _createElementVNode("button", {
                type: "button",
                class: "asale__close",
                "aria-label": "특별할인 닫기",
                disabled: saving.value,
                onClick: closeEditor
              }, [
                _createVNode(AppIcon, {
                  icon: "mdi-close",
                  size: 20
                })
              ], 8, _hoisted_21)
            ]),
            _createElementVNode("div", _hoisted_22, [
              _createElementVNode("label", _hoisted_23, [
                _cache[7] || (_cache[7] = _createElementVNode("span", null, "이름", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editor.value.name) = $event)),
                  type: "text",
                  maxlength: "80",
                  placeholder: "추석명절, 설명절, 어린이날 또는 2026-09-01"
                }, null, 512), [
                  [
                    _vModelText,
                    editor.value.name,
                    void 0,
                    { trim: true }
                  ]
                ])
              ]),
              _createElementVNode("div", _hoisted_24, [
                _createElementVNode("label", _hoisted_25, [
                  _cache[8] || (_cache[8] = _createElementVNode("span", null, "시작일", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editor.value.start) = $event)),
                    type: "date"
                  }, null, 512), [
                    [_vModelText, editor.value.start]
                  ])
                ]),
                _createElementVNode("label", _hoisted_26, [
                  _cache[9] || (_cache[9] = _createElementVNode("span", null, "종료일", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((editor.value.end) = $event)),
                    type: "date"
                  }, null, 512), [
                    [_vModelText, editor.value.end]
                  ])
                ])
              ]),
              (formError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_27, _toDisplayString(formError.value), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("footer", _hoisted_28, [
              _createElementVNode("button", {
                type: "button",
                class: "sui-btn sui-btn--ghost",
                disabled: saving.value,
                onClick: closeEditor
              }, "닫기", 8, _hoisted_29),
              _createElementVNode("button", {
                type: "button",
                class: "sui-btn sui-btn--primary",
                disabled: saving.value,
                onClick: onSave
              }, _toDisplayString(saving.value ? '저장 중…' : '저장'), 9, _hoisted_30)
            ])
          ])
        ], 40, _hoisted_17))
      : _createCommentVNode("", true)
  ]))
}
}

})